import { Button, Flex, Typography } from "@aircall/tractor";
import { useTranslation } from "react-i18next";

import { ScoreCardMenu } from "../ScoreCardMenu/ScoreCardMenu";

import { ScorecardPreCreateModal } from "@/components/ScorecardPreCreateModal";
import { SCORECARDS_ROUTE } from "@/constants/routes.constants";
import { ScoreCardFragment } from "@/graphql/generated/graphql";
import { redirectToDashboard } from "@/helpers/navigation/navigation.helpers";
import { useCreateScoreCardFlow } from "@/hooks/useCreateScoreCardFlow/useCreateScoreCardFlow";
import { useGetScorecards } from "@/hooks/useGetScorecards/useGetScorecards";
import { EvaluationEmptyState } from "../EvaluationEmptyState";
import { LoadingView } from "../LoadingView";
import { ScoreCardEmptyDropdownMenu } from "../ScoreCardEmptyDropdownMenu/ScoreCardEmptyDropdownMenu";
import { useTracking } from "./EvaluationScorecards.hooks";

function ScorecardsList({ scorecards }: { scorecards: ScoreCardFragment[] }) {
  const { t } = useTranslation();
  const { onClick: handleOnClick } = useTracking();

  const handleOnClickManage = () => {
    redirectToDashboard(SCORECARDS_ROUTE);
  };

  return (
    <Flex
      flex={1}
      flexDirection={"column"}
      gap="xs"
      alignItems="start"
      data-test="evaluation-empty-score-card-list"
      px="m"
      h="100%"
    >
      <Flex justifyContent="space-between" alignItems="center" w="100%">
        <Typography variant="bodySemiboldM">
          {`${t("evaluation.empty_view.select_scorecard")}:`}
        </Typography>
        <Button
          mode="link"
          onClick={handleOnClickManage}
          data-test="manage-scorecards-button"
        >
          {t("evaluation.empty_view.manage_scorecard")}
        </Button>
      </Flex>
      <ScoreCardMenu data={scorecards} onClickItem={handleOnClick} />
    </Flex>
  );
}

/**
 * fetch the scorecard list
 * render empty view if the list is empty, otherwise render the score card list
 * @returns
 */
export function EvaluationScorecards() {
  const { t } = useTranslation();
  const handleOnClickManage = () => {
    redirectToDashboard(SCORECARDS_ROUTE);
  };
  const {
    isPreCreateModalOpen,
    hideModal,
    onPreCreateFormSubmit,
    onCreateButtonClick,
  } = useCreateScoreCardFlow();

  const { loading, scorecards } = useGetScorecards({ isPublished: true });

  if (loading) {
    return (
      <LoadingView
        h="100%"
        justifyContent="center"
        alignItems="center"
        mt={0}
      />
    );
  }

  if (!scorecards || scorecards.length === 0) {
    return (
      <>
        <EvaluationEmptyState title={t("evaluation.empty_view.title")}>
          <Flex flexDirection="column" alignItems="center" gap="8px">
            <ScoreCardEmptyDropdownMenu
              onCreateButtonClick={onCreateButtonClick}
            />
            <Button
              mode="link"
              onClick={handleOnClickManage}
              data-test="manage-scorecards-button"
            >
              {t("evaluation.empty_view.manage_scorecard")}
            </Button>
          </Flex>
        </EvaluationEmptyState>
        <ScorecardPreCreateModal
          show={isPreCreateModalOpen}
          onHide={hideModal}
          onSubmit={onPreCreateFormSubmit}
        />
      </>
    );
  }

  return <ScorecardsList scorecards={scorecards} />;
}
